/**
 * The response directives (ADR-23 layer 2).
 *
 * `remark-directive` is a CommonMark-compatible extension: `::name` parses as a leaf directive,
 * degrades to plain text while streaming, evaluates nothing, and needs no compiler in the
 * browser — which is what made it the answer to MDX rather than MDX itself. This plugin is the
 * whole bridge from that syntax to components: the directive names the portal implements are
 * recognised, and everything else is put back exactly as the model typed it.
 *
 * Putting an unknown directive back as text is not politeness, it is the failure mode. Without a
 * `hName`, `mdast-util-to-hast` drops a directive node silently, so a mistyped `::fils` would
 * take a line of the answer with it — the same class of defect as the emptied `artifact:` href.
 */

/** @desc The directive the portal understands: the turn's deliverables, as a files section. */
export const FILES_DIRECTIVE_NAME = 'files';

/** @desc The hast tag `::files` becomes, mapped to `FilesSummary` in MdContent's component map. */
export const FILES_DIRECTIVE_TAG = 'files-summary';

/** @desc `::gallery{ids="…"}` — a grid of thumbnails for the images the model names. */
export const GALLERY_DIRECTIVE_NAME = 'gallery';

/** @desc The hast tag `::gallery` becomes, mapped to `ImageGallery`. */
export const GALLERY_DIRECTIVE_TAG = 'image-gallery';

/**
 * The slice of mdast this plugin rewrites. Structural rather than `@types/mdast`, matching the
 * house style of `rehype-reveal-sections`: three fields decide the question, and the walk below
 * is shorter than the dependency would be.
 */
export type MdastNode = {
  type: string;
  name?: string;
  value?: string;
  attributes?: Record<string, string | null | undefined>;
  data?: { hName?: string; hProperties?: Record<string, unknown> };
  children?: MdastNode[];
};

const DIRECTIVE_TYPES = new Set(['leafDirective', 'containerDirective', 'textDirective']);

/** @desc How a directive was written, for putting an unrecognised one back verbatim. */
const literalOf = (node: MdastNode): string =>
  `${node.type === 'textDirective' ? ':' : '::'}${node.name ?? ''}`;

export function remarkResponseDirectives() {
  return (tree: MdastNode): void => {
    const walk = (node: MdastNode): void => {
      if (DIRECTIVE_TYPES.has(node.type)) {
        if (node.type === 'leafDirective' && node.name === FILES_DIRECTIVE_NAME) {
          node.data = { ...node.data, hName: FILES_DIRECTIVE_TAG };
          return;
        }

        if (node.type === 'leafDirective' && node.name === GALLERY_DIRECTIVE_NAME) {
          // The raw attribute, unparsed: which ids are usable is a question about the turn's
          // artefacts, which only the component (and the backend's hygiene check) can answer.
          node.data = {
            ...node.data,
            hName: GALLERY_DIRECTIVE_TAG,
            hProperties: { ids: node.attributes?.ids ?? '' },
          };
          return;
        }

        // Anything else is text the model wrote. Rendering it as its own literal source keeps a
        // typo visible instead of deleting the line it was on.
        node.data = { ...node.data, hName: node.type === 'textDirective' ? 'span' : 'p' };
        node.children = [{ type: 'text', value: literalOf(node) }];
        return;
      }

      node.children?.forEach(walk);
    };

    walk(tree);
  };
}
